import React, { useMemo, useState } from 'react';
import { CaretDownIcon } from '@/components/Icons';
import cn from '@/utils/cn';

const YEARS_PER_PAGE = 12;
const YEARS_PER_ROW = 3;

const getDecadeStart = (year: number): number => Math.floor(year / 10) * 10;

interface YearPickerProps {
  selectedYear: number | null;
  onSelectYear: (year: number) => void;
  onToday: () => void;
  onClear: () => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  className?: string;
}

const YearPicker: React.FC<YearPickerProps> = ({
  selectedYear,
  onSelectYear,
  onToday,
  onClear,
  minDate,
  maxDate,
  className
}) => {
  const currentYear = new Date().getFullYear();
  const minYear = minDate?.getFullYear();
  const maxYear = maxDate?.getFullYear();

  const [decadeStart, setDecadeStart] = useState(() => getDecadeStart(selectedYear ?? currentYear));

  const rows = useMemo(() => {
    const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => decadeStart + i);
    const result: number[][] = [];
    for (let i = 0; i < years.length; i += YEARS_PER_ROW) {
      result.push(years.slice(i, i + YEARS_PER_ROW));
    }
    return result;
  }, [decadeStart]);

  const isYearDisabled = (year: number) =>
    (minYear !== undefined && year < minYear) || (maxYear !== undefined && year > maxYear);

  const isPrevDisabled = minYear !== undefined && decadeStart - 1 < minYear;
  const isNextDisabled = maxYear !== undefined && decadeStart + YEARS_PER_PAGE > maxYear;

  const handleToday = () => {
    setDecadeStart(getDecadeStart(currentYear));
    onToday();
  };

  return (
    <div
      className={cn(
        'flex w-56 flex-col gap-y-2 p-4 bg-white rounded-lg',
        'shadow-[0px_2px_4px_-2px_rgba(0,0,0,0.05),0px_4px_6px_-1px_rgba(0,0,0,0.10)]',
        className
      )}>
      <div className="flex items-center justify-between py-0.5">
        <button
          type="button"
          aria-label="Previous years"
          disabled={isPrevDisabled}
          onClick={() => setDecadeStart((prev) => prev - YEARS_PER_PAGE)}
          className={cn('text-emerge-navy', isPrevDisabled && 'opacity-30 cursor-not-allowed')}>
          <CaretDownIcon size="xs" className="rotate-90" />
        </button>
        <span className="text-sm font-medium text-emerge-navy">
          {decadeStart}-{decadeStart + YEARS_PER_PAGE - 1}
        </span>
        <button
          type="button"
          aria-label="Next years"
          disabled={isNextDisabled}
          onClick={() => setDecadeStart((prev) => prev + YEARS_PER_PAGE)}
          className={cn('text-emerge-navy', isNextDisabled && 'opacity-30 cursor-not-allowed')}>
          <CaretDownIcon size="xs" className="-rotate-90" />
        </button>
      </div>

      <div className="flex flex-col gap-y-2">
        {rows.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-3 gap-x-4">
            {row.map((year) => {
              const disabled = isYearDisabled(year);
              const isSelected = year === selectedYear;
              return (
                <button
                  key={year}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelectYear(year)}
                  aria-current={isSelected ? 'date' : undefined}
                  className={cn(
                    'py-2 rounded-full text-xs font-medium text-center text-emerge-navy hover:bg-emerge-base-2',
                    isSelected && 'bg-emerge-teal hover:bg-emerge-teal',
                    disabled && 'text-aux-disablegray-2 cursor-not-allowed hover:bg-transparent'
                  )}>
                  {year}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-x-4">
        <button
          type="button"
          onClick={handleToday}
          className="flex-1 py-2 px-3 rounded-full bg-emerge-teal text-xs font-medium text-emerge-navy hover:bg-dark-teal-30">
          Today
        </button>
        <button
          type="button"
          onClick={onClear}
          className="flex-1 py-2 px-3 rounded-full border border-emerge-navy-4 text-xs font-medium text-emerge-navy hover:bg-emerge-base-2">
          Clear
        </button>
      </div>
    </div>
  );
};

export default YearPicker;
